// ---------------------------------------------------------------------------
// cascadeTaskDueDates
//
// Computes the full set of task due-date updates needed when the user changes
// the due date of a single "anchor" task.  Every other incomplete task that
// already has a due_at is shifted by the same delta (milliseconds) as the
// anchor, preserving relative spacing between tasks.
//
// Rules:
//   - Completed items (completed_at != null) are never shifted.
//   - Items with no due_at (other than the anchor) are skipped — there is no
//     prior date to anchor a relative shift to.
//   - De-duped by taskId — multiple checklist items can share one task row;
//     only one PATCH is queued per taskId.
//   - If the anchor itself has no prior due_at, a cascade delta cannot be
//     computed.  Only the anchor is returned so it at least gets its date set.
//
// @param {object}        opts
// @param {Array}         opts.items         - All checklist items (today + later + done).
// @param {number|string} opts.anchorTaskId  - taskId of the item the user changed.
// @param {string|null}   opts.oldDueAt      - ISO string of the anchor's previous due_at.
// @param {Date}          opts.newDueAt      - The new Date the user selected.
//
// @returns {{ taskId: number|string, newDueAt: Date }[]}
// ---------------------------------------------------------------------------
export default function cascadeTaskDueDates({ items = [], anchorTaskId, oldDueAt, newDueAt }) {
    // Without a prior due_at on the anchor, we cannot compute a meaningful
    // delta for other tasks — just return the anchor change alone.
    if (!oldDueAt) {
        return [{ taskId: anchorTaskId, newDueAt }];
    }

    const deltaMs = newDueAt.getTime() - new Date(oldDueAt).getTime();

    // Build a deduped Map<taskId, Date> via reduce.
    // First occurrence of each taskId wins; completed items and undated non-anchors are skipped.
    const tasksToShift = items.reduce((acc, item) => {
        if (item.completed_at != null || acc.has(item.taskId)) return acc;
        if (item.taskId === anchorTaskId) {
            acc.set(item.taskId, newDueAt);
        } else if (item.due_at) {
            acc.set(item.taskId, new Date(new Date(item.due_at).getTime() + deltaMs));
        }
        return acc;
    }, new Map());

    return Array.from(tasksToShift, ([taskId, date]) => ({ taskId, newDueAt: date }));
}
